import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { siteLogo as logo } from "../../const";
import MobileHeader from "../../ComponentsWeb/Sub/MobileHeader";
import moment from "moment";
import { getAudioMimeType, isAudio, isImage, isVideo } from "../../const";
import ShowImage from "../../Components/ShowImage";
import { useDispatch, useSelector } from "react-redux";
import { adminUserChat, resetAdminUserChat, searchChatMessages } from "../../reducers/commonReducer";
import ReplyPreview from "./ReplyPreview";
import LinkifiedText from "../../Components/LinkifiedText";

const AdminChat = () => {
  const dispatch = useDispatch();
  const { adminUserChatList = [] } = useSelector((state) => state.commonReducer);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);
  const [showSearch, setShowSearch] = useState(false);

  const toggleSearch = () => {
    setShowSearch((prev) => {
      if (prev) setSearchQuery("");
      return !prev;
    });
  };
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const copyMessageText = (msg) => {
    const text = msg?.content?.trim();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      const id = msg?._id || msg?.id;
      setCopiedMessageId(id);
      setTimeout(() => setCopiedMessageId((cur) => (cur === id ? null : cur)), 2000);
    });
  };

  const queryParameters = new URLSearchParams(window.location.search);
  const agent = queryParameters.get("agent");
  const userid = queryParameters.get("id");
  const firstUserid = queryParameters.get("firstUserid");
  const name = queryParameters.get("name");
  const profileImg = queryParameters.get("img");
  const time = queryParameters.get("time");
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const [dbSearchMatches, setDbSearchMatches] = useState([]);
  const searchMatches = dbSearchMatches;

  const pageRef = useRef(page);
  const hasMoreRef = useRef(hasMore);
  const scrollSearchAbortRef = useRef(false);

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  const activeSearchMatch =
    searchMatches.length > 0 ? searchMatches[activeSearchIndex] : null;
  const activeSearchMessageId = activeSearchMatch?.messageId || null;
  // Initial fetch
  useEffect(() => {
    const fetchInitialMessages = async () => {
      try {
        await dispatch(resetAdminUserChat());

        const response = await dispatch(
          adminUserChat({
            firstUserId: firstUserid,
            secondUserId: userid,
            page: 1,
          })
        );

        if (response?.payload?.data?.length) {
          setPage(2); // Prepare for next page
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Initial fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialMessages();
  }, [dispatch, firstUserid, userid]);

  useEffect(() => {
    if (!normalizedSearchQuery) {
      setActiveSearchIndex(0);
      return;
    }

    setActiveSearchIndex(0);
  }, [normalizedSearchQuery]);

  useEffect(() => {
    if (!searchMatches.length) {
      return;
    }

    if (activeSearchIndex > searchMatches.length - 1) {
      setActiveSearchIndex(searchMatches.length - 1);
    }
  }, [activeSearchIndex, searchMatches]);

  // Debounced DB search — replaces local-only useMemo search
  useEffect(() => {
    if (!normalizedSearchQuery) {
      setDbSearchMatches([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await dispatch(searchChatMessages({
          firstUserId: firstUserid,
          secondUserId: userid,
          searchQuery: normalizedSearchQuery,
        }));
        const msgs = result?.payload?.data || [];
        setDbSearchMatches(msgs.map(m => ({
          messageId: String(m._id || m.id || ''),
          content: m.content || '',
        })));
        setActiveSearchIndex(msgs.length > 0 ? msgs.length - 1 : 0);
      } catch (err) {
        console.error('Chat search error:', err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [normalizedSearchQuery, dispatch, firstUserid, userid]);

  // Scroll to active match; auto-load older pages if message not yet rendered
  useEffect(() => {
    if (!activeSearchMessageId) return;

    const elementId = `admin-chat-message-${activeSearchMessageId}`;
    const el = document.getElementById(elementId);
    if (el) {
      setTimeout(() => {
        document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    scrollSearchAbortRef.current = false;
    (async () => {
      for (let i = 0; i < 30; i++) {
        if (scrollSearchAbortRef.current || !hasMoreRef.current) break;
        const response = await dispatch(adminUserChat({
          firstUserId: firstUserid,
          secondUserId: userid,
          page: pageRef.current,
        }));
        if (response?.payload?.data?.length) {
          setPage(p => p + 1);
        } else {
          setHasMore(false);
          hasMoreRef.current = false;
          break;
        }
        await new Promise(r => setTimeout(r, 150));
        if (scrollSearchAbortRef.current) break;
        const found = document.getElementById(elementId);
        if (found) {
          setTimeout(() => {
            document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
          return;
        }
      }
    })();

    return () => { scrollSearchAbortRef.current = true; };
  }, [activeSearchMessageId, dispatch, firstUserid, userid]);

  // Load older messages
  const loadMoreMessages = async () => {
    setLoadingMore(true);
    try {
      const response = await dispatch(
        adminUserChat({
          firstUserId: firstUserid,
          secondUserId: userid,
          page: pageRef.current,
        })
      );
      if (response?.payload?.data?.length) {
        setPage((prev) => prev + 1);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Load more error:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const moveSearchSelection = (direction) => {
    if (!searchMatches.length) {
      return;
    }

    setActiveSearchIndex((currentIndex) => {
      if (direction === "prev") {
        return currentIndex === 0 ? searchMatches.length - 1 : currentIndex - 1;
      }

      return currentIndex === searchMatches.length - 1 ? 0 : currentIndex + 1;
    });
  };

  return (
    <>
      <MobileHeader
        logo={profileImg || logo}
        name={name}
        time={time}
        agent={agent}
        showSearch={showSearch}
        onSearchToggle={toggleSearch}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchCount={normalizedSearchQuery ? `${searchMatches.length ? activeSearchIndex + 1 : 0}/${searchMatches.length}` : "0/0"}
        searchMatchesLength={searchMatches.length}
        onSearchPrev={() => moveSearchSelection("prev")}
        onSearchNext={() => moveSearchSelection("next")}
      />

      <div className="container-fluid admin-mobile-bg">
        <div
          className="ms-2 me-2 mbl-pedding-10"
          
        >
          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="grow" />
            </div>
          ) : (
            <>
              {hasMore && (
                <div className="text-center mb-3">
                  <button
                    className="btn btn-sm"
                    onClick={loadMoreMessages}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading..." : (
                      <>
                        <i className="bi bi-arrow-repeat"></i> Syncing older messages. Click Here
                      </>
                    )}
                  </button>
                </div>
              )}
              {adminUserChatList.map((msg, index) => {
                const messageKey = msg?._id || msg?.id;
                const showDate =
                  index === 0 ||
                  !moment(msg.createdAt).isSame(adminUserChatList[index - 1]?.createdAt, "day");

                const isSender = msg.sender === firstUserid;
                const isStickerMessage =
                  msg?.messageType === "sticker" && msg?.files?.length > 0;

                return (
                  <React.Fragment key={msg._id || index}>
                    {showDate && (
                      <Row className="justify-content-center">
                        <div className="text-center my-2">
                          <small className="text-muted text-secondary">
                            {moment(msg.createdAt).format("D MMMM")}
                          </small>
                        </div>
                      </Row>
                    )}

                    <Row className="mt-1">
                      <div className="px-0">
                        <div
                          id={`admin-chat-message-${messageKey}`}
                          className={
                            isSender
                              ? `message m-w-50 mb-2 ${activeSearchMessageId === messageKey ? "chat-message-highlight" : ""}`
                              : `reciver-message m-w-50 bg-light-gray mb-2 ${activeSearchMessageId === messageKey ? "chat-message-highlight" : ""}`
                          }
                        >
                          {msg?.content?.trim() ? (
                            <div className="chat-message-actions">
                              <button
                                type="button"
                                className="chat-message-action-btn"
                                onClick={() => copyMessageText(msg)}
                                aria-label="Copy message"
                                title="Copy"
                              >
                                <i className={`bi ${copiedMessageId === messageKey ? "bi-check2" : "bi-clipboard"}`}></i>
                              </button>
                            </div>
                          ) : null}
                          <ReplyPreview
                            replyTo={msg?.replyTo}
                            title="Replied message"
                            isOwnMessage={isSender}
                            className="chat-message-reply"
                          />
                          {msg?.files?.length > 0 && (
                            <Row className={isStickerMessage ? "chat-sticker-stack" : ""}>
                              {msg.files.map((src, idx) => (
                                <Col className="text-center" key={idx}>
                                  {isImage(src?.cloudUrl) ? (
                                    <ShowImage
                                      img={src?.cloudUrl}
                                      alt={src?.fileName}
                                      width={isStickerMessage ? "chat-sticker-img" : "chat-img"}
                                    />
                                  ) : isVideo(src?.cloudUrl) ? (
                                    <video width="100%" controls>
                                      <source src={src?.cloudUrl} type="video/mp4" />
                                    </video>
                                  ) : isAudio(src?.cloudUrl) ? (
                                    <audio
                                      controls
                                      preload="metadata"
                                      className="chat-audio-player"
                                    >
                                      <source
                                        src={src?.cloudUrl}
                                        type={getAudioMimeType(src?.cloudUrl, src?.fileType)}
                                      />
                                    </audio>
                                  ) : (
                                    <span>Unsupported file type</span>
                                  )}
                                </Col>
                              ))}
                            </Row>
                          )}
                          <p className={`mb-0 ${isStickerMessage ? "chat-sticker-caption" : ""}`}>
                            {msg?.content?.trim() ? (
                              <LinkifiedText
                                text={msg?.content}
                                highlight={searchQuery}
                              />
                            ) : null}
                            {msg?.content?.trim() ? <br /> : null}
                            <span className={`m-f-13 float-${isSender ? "right" : "left"}`}>
                              {moment(msg.createdAt).format("hh:mm A")}{" "}
                              {isSender && (
                                <i className={`bi bi-check2-all ${msg?.isRead ? "text-primary" : ""}`}></i>
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    </Row>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminChat;
